import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAPI } from "/src/hooks/useAPI";

export function useSimulationData(apiBaseUrl, authToken, enabled = true) {
  const { callAPI } = useAPI();
  const queryClient = useQueryClient();

  const currentPath = window.location.pathname.toLowerCase();
  const is2FA = currentPath.includes("2fa");

  // Helper to determine if we should actually run the queries
  const shouldFetch = enabled && !!authToken && !!apiBaseUrl && !is2FA;

  // 1. Fetch all simulations
  const simulationsQuery = useQuery({
    queryKey: ["simulations", authToken],
    queryFn: () => callAPI(`optimized_get_simulations`, { token: authToken }),
    enabled: shouldFetch,
  });

  // 2. Fetch recent simulations with evaluation
  const recentEvalQuery = useQuery({
    queryKey: ["simulations", "recent-eval", authToken],
    queryFn: () =>
      callAPI(`recent_simulations_with_evaluation?limit=5`, {
        token: authToken,
      }),
    enabled: shouldFetch,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnMount: true, // Refetch on mount if data is stale
    refetchIntervalInBackground: false,
  });

  // 3. Ad-hoc fetcher (Imperative style via queryClient or a separate useQuery)
  // Note: For "on-demand" clicks, we use queryClient.fetchQuery
  const fetchRecentSimulationsForScenario = async (scenarioId, limit = 5) => {
    if (!shouldFetch) return [];
    try {
      return await queryClient.fetchQuery({
        queryKey: ["simulations", "scenario", scenarioId, limit],
        queryFn: () =>
          callAPI(
            `recent_simulations_with_evaluation?scenario_id=${scenarioId}&limit=${limit}`,
            { token: authToken }
          ),
        enabled: shouldFetch,
        staleTime: 10 * 60 * 1000, // 10 minutes
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchOnMount: true, // Refetch on mount if data is stale
        refetchIntervalInBackground: false,
      });
    } catch (error) {
      console.error("Error fetching recent simulations for scenario:", error);
      return [];
    }
  };

  // 4. Manual refresh trigger (refetches both queries)
  const refreshSimulations = () => {
    queryClient.invalidateQueries({ queryKey: ["simulations"] });
  };

  // 5. Compute derived state (replaces your old useMemo)
  const simulationsData = useMemo(() => {
    const all = simulationsQuery.data || [];
    const recentWithEvaluation = recentEvalQuery.data || [];

    const completed = all.filter(
      (sim) => sim.status === "completed" && sim.evaluation !== null
    );

    const recentCompleted = [...completed]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);

    const inProgress = all.filter((sim) => sim.status !== "completed");

    return {
      all,
      completed,
      recentCompleted,
      inProgress,
      loading: simulationsQuery.isLoading,
      lastFetch: simulationsQuery.dataUpdatedAt,
      recentWithEvaluation,
      recentEvaluationLoading: recentEvalQuery.isLoading,
      isError: simulationsQuery.isError || recentEvalQuery.isError,
    };
  }, [
    simulationsQuery.data,
    simulationsQuery.isLoading,
    recentEvalQuery.data,
    recentEvalQuery.isLoading,
  ]);

  return {
    simulationsData,
    refreshSimulations,
    fetchRecentSimulationsForScenario,
  };
}
